import { Store } from "@reduxjs/toolkit";
import { QueryClient } from "react-query";
import { NavigateFunction } from "react-router-dom";
import { Persistor } from "redux-persist";

import { sessionModalTracker } from "../Utils/sessionModalTracker";

/**
 * Navigation function type - can be React Router's navigate or a custom function
 */
type NavigationFunction = NavigateFunction | ((path: string) => void);

/**
 * Options for state cleanup (no navigation)
 */
export interface StateCleanupOptions {
  store?: Store;
  persistor?: Persistor;
  /**
   * React Query client for clearing cached data (optional)
   */
  queryClient?: QueryClient;
  /**
   * Redux reset actions to dispatch on logout (e.g., resetAuthState, resetBizState, resetAllDocuments).
   * Each app should pass its own set of reset actions for all Redux slices including auth.
   */
  resetActions?: Array<() => { type: string }>;
}

/**
 * Options for performing a complete logout cleanup including navigation
 */
interface LogoutCleanupOptions extends StateCleanupOptions {
  navigate: NavigationFunction;
  /**
   * Delay in milliseconds before navigation (useful for showing toasts)
   * @default 0
   */
  delayBeforeNavigation?: number;
}

/**
 * Clears auth-related localStorage keys while preserving user preferences.
 * Use this instead of localStorage.clear() to avoid nuking unrelated data.
 */
export const clearAuthLocalStorage = (): void => {
  const keysToRemove = ["userType", "onboardingModalSeen", "lastActiveTab"];
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("onboarding_completed_shown_")) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Performs complete logout cleanup including Redux state, storage, and navigation.
 * This is the single source of truth for logout cleanup logic across the application.
 *
 * Used by:
 * - Manual logout (NavBar components)
 * - Session expiry (useJwtExpiry hook)
 * - Session invalidation (axiosSessionInterceptor)
 *
 * @param options - Configuration for logout cleanup
 * @param options.navigate - React Router navigate function
 * @param options.store - Redux store (optional, for state reset)
 * @param options.resetActions - Redux actions to reset all state slices (optional)
 * @param options.persistor - Redux Persist persistor (optional, for purging)
 *
 * @example
 * // Simple usage (storage cleanup + navigation only)
 * performLogoutCleanup({ navigate });
 *
 * @example
 * // Complete usage (with Redux reset)
 * performLogoutCleanup({
 *   navigate,
 *   store,
 *   resetActions: adminResetActions,
 *   persistor
 * });
 */
/**
 * Performs state cleanup only (no navigation).
 *
 * Used by the Axios interceptor and WebSocket handler where navigation
 * is handled separately (e.g., after showing a session-kicked overlay).
 *
 * This is the single source of truth for wiping client state.
 * performLogoutCleanup delegates to this function internally.
 */
export const performStateCleanup = async ({
  store,
  resetActions,
  persistor,
  queryClient,
}: StateCleanupOptions): Promise<void> => {
  // Reset Redux state if provided
  if (store && resetActions) {
    resetActions.forEach((action) => store.dispatch(action()));
  }

  // Purge Redux Persist storage to prevent rehydration
  if (persistor) {
    await persistor.purge();
  }

  // Clear React Query cache if provided
  if (queryClient) {
    queryClient.clear();
  }

  // Clear all sessionStorage (all keys are auth/session-related)
  sessionStorage.clear();

  // Clear auth-related localStorage keys
  clearAuthLocalStorage();

  // Clear tracked session-critical modals
  sessionModalTracker.clearAll();
};

/**
 * Performs complete logout cleanup including Redux state, storage, and navigation.
 * This is the single source of truth for logout cleanup logic across the application.
 *
 * Used by:
 * - Manual logout (NavBar components)
 * - Session expiry (useJwtExpiry hook)
 * - Token refresh failure (useJwtExpiry hook)
 *
 * @param options - Configuration for logout cleanup
 * @param options.navigate - React Router navigate function
 * @param options.store - Redux store (optional, for state reset)
 * @param options.resetActions - Redux actions to reset all state slices (optional)
 * @param options.persistor - Redux Persist persistor (optional, for purging)
 *
 * @example
 * // Simple usage (storage cleanup + navigation only)
 * performLogoutCleanup({ navigate });
 *
 * @example
 * // Complete usage (with Redux reset)
 * performLogoutCleanup({
 *   navigate,
 *   store,
 *   resetActions: adminResetActions,
 *   persistor
 * });
 */
export const performLogoutCleanup = async ({
  navigate,
  store,
  persistor,
  queryClient,
  resetActions,
  delayBeforeNavigation = 0,
}: LogoutCleanupOptions): Promise<void> => {
  // Wipe all client state (Redux, Persist, QueryClient, storage, modals)
  await performStateCleanup({ store, resetActions, persistor, queryClient });

  // Check navigation context before proceeding
  const isOnLoginPage = window.location.pathname.includes("/login");
  const hasActiveSessionModal = sessionModalTracker.hasActiveModals();

  // Apply delay if specified (e.g., to show toast notifications)
  if (delayBeforeNavigation > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayBeforeNavigation));
  }

  // Navigate based on context
  // If on login page with active session modal, reload to clear modal state
  if (isOnLoginPage && hasActiveSessionModal) {
    window.location.reload();
  } else {
    navigate("/login");
  }
};
